import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { saveAs } from 'file-saver';
import JSZip from 'jszip';
import CodeRoundedIcon from '@mui/icons-material/CodeRounded';
import FolderZipOutlinedIcon from '@mui/icons-material/FolderZipOutlined';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';
import CodeBlock from './CodeBlock';
import '../Assets/css/MarkdownMessage.css';
import '../Assets/css/ChatUI.css';

// The backend puts the file name on the first line of each Terraform block, e.g. "# main.tf"
const FILE_NAME_RE = /^\s*(?:#|\/\/)\s*(?:File:\s*)?([\w.\-/]+\.(?:tf|tfvars|tfvars\.example))\s*$/i;
const CODE_BLOCK_RE = /```(?:hcl|terraform|tf)[^\n]*\n([\s\S]*?)```/g;

const getFileName = (code) => {
  const match = code.split('\n')[0].match(FILE_NAME_RE);
  return match ? match[1].split('/').pop() : null;
};

export const extractTerraformFiles = (markdown) => {
  const files = [];
  for (const match of (markdown || '').matchAll(CODE_BLOCK_RE)) {
    const name = getFileName(match[1]);
    if (name && !files.some((f) => f.name === name)) files.push({ name, content: match[1].replace(/\s+$/, '') + '\n' });
  }
  return files;
};

export const downloadFilesAsZip = async (files, zipName = 'terraform.zip') => {
  const zip = new JSZip();
  files.forEach((file) => zip.file(file.name, file.content));
  saveAs(await zip.generateAsync({ type: 'blob' }), zipName);
};

const markdownComponents = {
  pre: ({ children }) => {
    const codeElement = React.Children.toArray(children)[0];
    const className = codeElement?.props?.className || '';
    const language = (className.match(/language-([\w-]+)/) || [])[1];
    const code = String(codeElement?.props?.children ?? '').replace(/\n$/, '');
    return <CodeBlock language={language} code={code} fileName={getFileName(code)} />;
  },
  a: ({ node, children, ...props }) => (
    <a {...props} target="_blank" rel="noopener noreferrer">
      {children}
    </a>
  ),
};

// Renders review / Terraform answers: Markdown with tables and editor-style code blocks
const MarkdownMessage = ({ content, onOpenEditor }) => {
  const terraformFiles = extractTerraformFiles(content);

  return (
    <div className="markdown-message">
      {terraformFiles.length > 1 && (
        <div className="project-card">
          <div className="project-card-title">
            <span className="project-card-icon"><CodeRoundedIcon /></span>
            <span>
              Terraform project
              <div className="project-card-files">{terraformFiles.map((f) => f.name).join(' · ')}</div>
            </span>
          </div>
          <div className="project-card-actions">
            {onOpenEditor && (
              <button type="button" className="project-card-button primary" onClick={() => onOpenEditor(terraformFiles)}>
                <OpenInNewRoundedIcon sx={{ fontSize: 16 }} /> Open in editor
              </button>
            )}
            <button type="button" className="project-card-button" onClick={() => downloadFilesAsZip(terraformFiles)}>
              <FolderZipOutlinedIcon sx={{ fontSize: 16 }} /> Download all {terraformFiles.length} files (.zip)
            </button>
          </div>
        </div>
      )}
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
        {content}
      </ReactMarkdown>
    </div>
  );
};

export default MarkdownMessage;
